'use client';

import { useState, useCallback, ChangeEvent } from 'react';
import CRC32 from 'crc-32';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { crc32HashContent } from '../../data/crc32-hash-content';
import { Copy, Check, RefreshCw, Upload, FileText, X, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

type TabValue = 'text' | 'file';

interface FileInfo {
    name: string;
    size: number;
    type: string;
}

export default function Crc32HashGenerator() {
    const [activeTab, setActiveTab] = useState<TabValue>('text');
    const [textInput, setTextInput] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);
    const [hash, setHash] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const [isUpperCase, setIsUpperCase] = useState(false);
    const { toast } = useToast();

    // Generate CRC32 hash
    const generateHash = useCallback(async () => {
        if (activeTab === 'text' && !textInput.trim()) {
            toast({
                title: 'Error',
                description: 'Please enter some text to hash',
                variant: 'destructive',
            });
            return;
        }

        if (activeTab === 'file' && !file) {
            toast({
                title: 'Error',
                description: 'Please select a file to hash',
                variant: 'destructive',
            });
            return;
        }

        try {
            setIsLoading(true);
            let result = '';

            if (activeTab === 'text') {
                const crc = CRC32.str(textInput);
                result = (crc >>> 0).toString(16).padStart(8, '0');
            } else if (file) {
                const buffer = await file.arrayBuffer();
                const uint8Array = new Uint8Array(buffer);
                const crc = CRC32.buf(Array.from(uint8Array));
                result = (crc >>> 0).toString(16).padStart(8, '0');
            }

            setHash(isUpperCase ? result.toUpperCase() : result.toLowerCase());
            setIsCopied(false);
        } catch (error) {
            console.error('Error generating CRC32 hash:', error);
            toast({
                title: 'Error',
                description: 'Failed to generate CRC32 hash',
                variant: 'destructive',
            });
            setHash('');
        } finally {
            setIsLoading(false);
        }
    }, [activeTab, textInput, file, isUpperCase, toast]);

    // Handle file selection
    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0] || null;
        if (selectedFile) {
            setFile(selectedFile);
            setFileInfo({
                name: selectedFile.name,
                size: selectedFile.size,
                type: selectedFile.type || 'Unknown',
            });
            setHash('');
        }
    };

    // Remove file
    const removeFile = () => {
        setFile(null);
        setFileInfo(null);
        setHash('');
    };

    // Copy hash to clipboard
    const copyToClipboard = () => {
        if (!hash) return;

        navigator.clipboard.writeText(hash);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);

        toast({
            title: 'Copied!',
            description: 'CRC32 hash has been copied to clipboard',
        });
    };

    // Reset all fields
    const resetForm = () => {
        setTextInput('');
        setFile(null);
        setFileInfo(null);
        setHash('');
        setIsCopied(false);
    };

    // Format file size for display
    const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };

    // Toggle uppercase
    const toggleUpperCase = () => {
        setIsUpperCase((prev) => {
            const newVal = !prev;
            if (hash) setHash(newVal ? hash.toUpperCase() : hash.toLowerCase());
            return newVal;
        });
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="max-w-4xl mx-auto space-y-6">
                <ToolPageIntro title="CRC32 Hash Generator">
                Generate CRC32 checksums from text or files. Common in file formats and network protocols. Instant results, no sign-up.
            </ToolPageIntro>

                <Tabs
                    defaultValue="text"
                    className="w-full"
                    onValueChange={(value) => setActiveTab(value as TabValue)}
                >
                    <TabsList className="grid w-full grid-cols-2 max-w-md">
                        <TabsTrigger value="text">Text Input</TabsTrigger>
                        <TabsTrigger value="file">File Upload</TabsTrigger>
                    </TabsList>

                    <TabsContent value="text" className="mt-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Generate from Text</CardTitle>
                                <CardDescription>Enter the text you want to generate a CRC32 hash for</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    <Label htmlFor="text-input">Input Text</Label>
                                    <Textarea
                                        id="text-input"
                                        value={textInput}
                                        onChange={(e) => setTextInput(e.target.value)}
                                        placeholder="Enter text to hash..."
                                        className="min-h-[120px]"
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="file" className="mt-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Generate from File</CardTitle>
                                <CardDescription>Upload a file to generate its CRC32 checksum</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="flex flex-col items-center space-y-4">
                                    <label
                                        htmlFor="file-upload"
                                        className={cn(
                                            'flex flex-col items-center justify-center w-full h-64 border-2 border-dashed rounded-lg cursor-pointer',
                                            'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700',
                                            'transition-colors duration-200 ease-in-out'
                                        )}
                                    >
                                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                            <Upload className="w-10 h-10 mb-3 text-gray-400" />
                                            <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                                <span className="font-semibold">Click to upload</span> or drag and drop
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">Any file type (Max: 100MB)</p>
                                        </div>
                                        <input
                                            id="file-upload"
                                            type="file"
                                            className="hidden"
                                            onChange={handleFileChange}
                                        />
                                    </label>

                                    {fileInfo && (
                                        <div className="w-full p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-2">
                                                    <FileText className="h-5 w-5 text-gray-500" />
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                            {fileInfo.name}
                                                        </p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                                            {formatFileSize(fileInfo.size)} • {fileInfo.type || 'Unknown type'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={removeFile}
                                                    className="text-gray-400 hover:text-gray-500"
                                                >
                                                    <X className="h-5 w-5" />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>

                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <Label htmlFor="uppercase" className="text-sm font-medium">
                            Uppercase
                        </Label>
                        <input
                            id="uppercase"
                            type="checkbox"
                            checked={isUpperCase}
                            onChange={toggleUpperCase}
                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                    </div>

                    <div className="flex space-x-2">
                        <Button variant="outline" onClick={resetForm} disabled={isLoading || (!textInput && !file)}>
                            <RefreshCw className="mr-2 h-4 w-4" />
                            Reset
                        </Button>
                        <Button
                            onClick={generateHash}
                            disabled={isLoading || (activeTab === 'text' ? !textInput.trim() : !file)}
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Generating...
                                </>
                            ) : (
                                'Generate CRC32 Hash'
                            )}
                        </Button>
                    </div>
                </div>

                {hash && (
                    <Card>
                        <CardHeader>
                            <CardTitle>CRC32 Hash Result</CardTitle>
                            <CardDescription>
                                {activeTab === 'file' && fileInfo
                                    ? `Checksum for ${fileInfo.name}`
                                    : 'Generated checksum'}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center space-x-2">
                                <Input id="hash-result" value={hash} readOnly className="font-mono flex-1" />
                                <Button
                                    type="button"
                                    size="icon"
                                    variant="outline"
                                    onClick={copyToClipboard}
                                    disabled={!hash}
                                >
                                    {isCopied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                                    <span className="sr-only">Copy hash</span>
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                <Card className="border-dashed">
                    <CardHeader>
                        <CardTitle>About CRC32</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-2">
                        <p>
                            <strong>CRC32</strong> (Cyclic Redundancy Check 32) is a checksum algorithm that produces a 32-bit (8 character)
                            hexadecimal value. It's commonly used to verify data integrity in network protocols and file formats.
                        </p>
                        <p>
                            <strong>Note:</strong> CRC32 is not cryptographically secure and should not be used for security purposes
                            like password hashing. It's designed for error detection, not for protecting against intentional tampering.
                        </p>
                        <p className="text-xs text-muted-foreground/80">
                            All hashing is performed in your browser. Your data never leaves your computer.
                        </p>
                    </CardContent>
                </Card>

                <div className="mt-16">
                    <InformationalContent
                        title={crc32HashContent.title}
                        description={crc32HashContent.description}
                        content={crc32HashContent.content}
                        faqs={crc32HashContent.faqs}
                        relatedTools={crc32HashContent.relatedTools}
                    />
                </div>
            </div>
        </div>
    );
}
